/**
 * Web Push helpers — register the service worker, subscribe to push, and
 * post the subscription back to the LEAGUE HUB backend.
 *
 * Why a separate file: keeps the SW registration out of the React tree so
 * we can call into it from anywhere (profile page, notification settings,
 * etc.) without worrying about effect order.
 */
import {
  fetchVapidPublicKey,
  subscribePush,
  unsubscribePush,
} from "./api";

const SW_URL = "/sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL);
    return reg;
  } catch (_e) {
    return null;
  }
}

export async function currentPushState() {
  if (!pushSupported()) return { supported: false, subscribed: false, permission: "default" };
  const permission = typeof Notification !== "undefined" ? Notification.permission : "default";
  const reg = await navigator.serviceWorker.getRegistration(SW_URL).catch(() => null);
  const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
  return {
    supported: true,
    permission,
    subscribed: Boolean(sub),
    endpoint: sub?.endpoint || null,
  };
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("Push not supported in this browser.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied"
      ? "Notifications blocked. Enable them in your browser settings."
      : "Permission dismissed.");
  }
  const reg = (await navigator.serviceWorker.getRegistration(SW_URL))
    || (await ensureServiceWorker());
  if (!reg) throw new Error("Service worker failed to register.");
  // Some browsers need the SW to be 'active' before subscribing.
  await navigator.serviceWorker.ready.catch(() => {});
  const { key } = await fetchVapidPublicKey();
  if (!key) throw new Error("Server has not configured VAPID keys.");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });
  const json = sub.toJSON();
  await subscribePush(json, navigator.userAgent.slice(0, 240));
  return { endpoint: sub.endpoint };
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.getRegistration(SW_URL).catch(() => null);
  const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
  if (sub) {
    try { await unsubscribePush(sub.endpoint); } catch (_e) { /* server cleanup */ }
    try { await sub.unsubscribe(); } catch (_e) { /* browser cleanup */ }
  }
}
